import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../../components/common/Layout';
import { Icon } from '../../components/common/Icon';
import { useTaskStore } from '../../store';
import type { AgentTask, TaskStatus } from '../../types/task';

/* ─── Status config ─── */
const statusConfig: Record<TaskStatus, { icon: string; color: string; bg: string; dot: string }> = {
  pending:     { icon: 'schedule',      color: 'text-gray-400',    bg: 'bg-gray-500/10',    dot: 'bg-gray-400' },
  in_progress: { icon: 'play_circle',   color: 'text-blue-400',    bg: 'bg-blue-500/10',    dot: 'bg-blue-500 animate-pulse' },
  done:        { icon: 'check_circle',  color: 'text-emerald-400', bg: 'bg-emerald-500/10', dot: 'bg-emerald-500' },
  failed:      { icon: 'error',         color: 'text-red-400',     bg: 'bg-red-500/10',     dot: 'bg-red-500' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/* ─── Demo team tasks ─── */
interface TeamTask {
  id: string;
  teamName: string;
  teamIcon: string;
  title: string;
  agents: { name: string; initial: string; role: string }[];
  status: TaskStatus;
  progress: number;
  stage?: string;
  reward?: string;
  deadline?: string;
  updatedAt: string;
}

const DEMO_TEAM_TASKS: TeamTask[] = [
  {
    id: 'tt1',
    teamName: 'AAAI 2026 论文组',
    teamIcon: 'school',
    title: '完成多智能体协作综述 Camera-Ready',
    agents: [
      { name: 'Sage', initial: 'S', role: '主笔·综述框架' },
      { name: 'Echo', initial: 'E', role: '文献交叉比对' },
    ],
    status: 'in_progress',
    progress: 78,
    stage: 'Camera-Ready 修订',
    reward: '¥2,400',
    deadline: '12天后',
    updatedAt: new Date(Date.now() - 1800_000).toISOString(),
  },
  {
    id: 'tt2',
    teamName: '数据分析组',
    teamIcon: 'analytics',
    title: '用户留存预测模型 v2 部署',
    agents: [
      { name: 'Cipher', initial: 'C', role: '模型构建' },
      { name: 'Atlas', initial: 'A', role: '数据采集·清洗' },
    ],
    status: 'in_progress',
    progress: 45,
    stage: '模型训练',
    reward: '¥1,200',
    updatedAt: new Date(Date.now() - 7200_000).toISOString(),
  },
  {
    id: 'tt3',
    teamName: '内容营销组',
    teamIcon: 'campaign',
    title: 'Q2 产品上线全渠道内容策略',
    agents: [
      { name: 'Nova', initial: 'N', role: '策略制定·内容日历' },
      { name: 'Atlas', initial: 'A', role: '渠道对接·合作映射' },
    ],
    status: 'done',
    progress: 100,
    stage: '已交付',
    reward: '¥800',
    updatedAt: new Date(Date.now() - 86400_000).toISOString(),
  },
];

/* ─── Section tabs ─── */
type SectionTab = 'my' | 'team' | 'received';

export function TasksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tasks, loading, fetchTasks } = useTaskStore();
  const [section, setSection] = useState<SectionTab>('my');

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const activeTasks = tasks.filter((t) => t.status === 'in_progress');
  const pendingTasks = tasks.filter((t) => t.status === 'pending');
  const completedTasks = tasks.filter((t) => t.status === 'done' || t.status === 'failed');

  const handleTaskClick = (task: AgentTask) => {
    navigate('/my-agent/chat', { state: { prefill: `@task #${task.id}` } });
  };

  /* ─── Stats summary ─── */
  const totalActive = activeTasks.length + DEMO_TEAM_TASKS.filter((t) => t.status === 'in_progress').length;
  const totalEarnings = DEMO_TEAM_TASKS.reduce((sum, t) => {
    if (t.reward && t.status === 'done') {
      const num = parseFloat(t.reward.replace(/[^\d.]/g, ''));
      return sum + (isNaN(num) ? 0 : num);
    }
    return sum;
  }, 0);

  return (
    <Layout showTabBar>
      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('nav.tasks', '任务')}
          </h1>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black p-3 text-center">
            <p className="text-lg font-bold text-blue-500">{totalActive}</p>
            <p className="text-[10px] text-gray-400">进行中</p>
          </div>
          <div className="rounded-xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black p-3 text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{tasks.length + DEMO_TEAM_TASKS.length}</p>
            <p className="text-[10px] text-gray-400">总任务</p>
          </div>
          <div className="rounded-xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black p-3 text-center">
            <p className="text-lg font-bold text-emerald-500">¥{totalEarnings.toLocaleString()}</p>
            <p className="text-[10px] text-gray-400">已结算</p>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-100 dark:border-white/5">
          {([
            { key: 'my' as SectionTab, label: '我的任务', icon: 'person', count: tasks.length },
            { key: 'team' as SectionTab, label: 'Team 协作', icon: 'groups', count: DEMO_TEAM_TASKS.length },
            { key: 'received' as SectionTab, label: '收到委托', icon: 'inbox', count: 0 },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSection(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                section === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  section === tab.key ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── My Tasks ── */}
        {section === 'my' && (
          <div className="space-y-2">
            {loading && tasks.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-primary rounded-full animate-spin mx-auto" />
              </div>
            )}

            {!loading && tasks.length === 0 && (
              <EmptyState icon="task_alt" text="暂无任务" hint="和 Agent 对话来创建任务" />
            )}

            {activeTasks.length > 0 && (
              <>
                <SectionLabel icon="play_circle" label="进行中" count={activeTasks.length} color="text-blue-500" />
                {activeTasks.map((task) => (
                  <TaskCard key={task.id} task={task} onClick={() => handleTaskClick(task)} />
                ))}
              </>
            )}

            {pendingTasks.length > 0 && (
              <>
                <SectionLabel icon="schedule" label="等待中" count={pendingTasks.length} color="text-gray-400" />
                {pendingTasks.map((task) => (
                  <TaskCard key={task.id} task={task} onClick={() => handleTaskClick(task)} />
                ))}
              </>
            )}

            {completedTasks.length > 0 && (
              <>
                <SectionLabel icon="check_circle" label="已完成" count={completedTasks.length} color="text-emerald-500" />
                {completedTasks.map((task) => (
                  <TaskCard key={task.id} task={task} onClick={() => handleTaskClick(task)} />
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Team Tasks ── */}
        {section === 'team' && (
          <div className="space-y-3">
            {DEMO_TEAM_TASKS.map((task) => (
              <TeamTaskCard key={task.id} task={task} />
            ))}
          </div>
        )}

        {/* ── Received commissions ── */}
        {section === 'received' && (
          <EmptyState icon="inbox" text="暂无收到的委托" hint="当其他用户委托你的 Agent 时会在这里显示" />
        )}
      </div>
    </Layout>
  );
}

/* ─── Sub-components ─── */

function SectionLabel({ icon, label, count, color }: { icon: string; label: string; count: number; color: string }) {
  return (
    <div className="flex items-center gap-1.5 pt-2 pb-1 px-0.5">
      <Icon name={icon} size={14} className={color} />
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-[10px] text-gray-400">{count}</span>
    </div>
  );
}

function EmptyState({ icon, text, hint }: { icon: string; text: string; hint: string }) {
  return (
    <div className="py-16 text-center">
      <Icon name={icon} size={40} className="text-gray-200 dark:text-gray-700 mx-auto mb-3" />
      <p className="text-sm text-gray-400 dark:text-gray-500">{text}</p>
      <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{hint}</p>
    </div>
  );
}

function TaskCard({ task, onClick }: { task: AgentTask; onClick: () => void }) {
  const status = task.status as TaskStatus;
  const cfg = statusConfig[status];

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 rounded-xl bg-white dark:bg-black border border-gray-100 dark:border-white/5 hover:border-gray-200 dark:hover:border-white/10 transition-colors"
    >
      {/* Agent identity */}
      <div className="flex items-center gap-2 mb-2">
        {task.agentAvatar ? (
          <img src={task.agentAvatar} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary/25 to-primary/5 flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] font-bold text-primary">{(task.agentName || 'A')[0]}</span>
          </div>
        )}
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{task.agentName || 'Agent'}</span>
        {task.partnerName && (
          <>
            <Icon name="sync_alt" size={12} className="text-gray-300 dark:text-gray-600" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{task.partnerName}</span>
          </>
        )}
        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto flex-shrink-0 tabular-nums">
          {timeAgo(task.updatedAt)}
        </span>
      </div>

      {/* Title */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1">
          {task.title}
        </span>
      </div>

      {/* Tags */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        {task.stage && (
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
            <Icon name={cfg.icon} size={11} />
            {task.stage}
          </span>
        )}
      </div>

      {status === 'in_progress' && (
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex-1 h-1 bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${task.progress}%` }} />
          </div>
          <span className="text-[10px] text-gray-400 tabular-nums">{task.progress}%</span>
        </div>
      )}

      {task.latestOutput && (
        <div className="flex items-start gap-1.5">
          <Icon name="terminal" size={12} className="text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 leading-snug">{task.latestOutput}</p>
        </div>
      )}
    </button>
  );
}

function TeamTaskCard({ task }: { task: TeamTask }) {
  const cfg = statusConfig[task.status];

  return (
    <div className="p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-black">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2.5">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name={task.teamIcon} size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-gray-400">{task.teamName}</p>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{task.title}</h4>
        </div>
        <span className="text-[10px] text-gray-400 tabular-nums">{timeAgo(task.updatedAt)}</span>
      </div>

      {/* Agent team */}
      <div className="flex items-center gap-1 mb-3 overflow-x-auto hide-scrollbar">
        {task.agents.map((agent) => (
          <div
            key={agent.name}
            className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-50 dark:bg-white/5 flex-shrink-0"
          >
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary/25 to-primary/5 flex items-center justify-center">
              <span className="text-[8px] font-bold text-primary">{agent.initial}</span>
            </div>
            <span className="text-[11px] text-gray-700 dark:text-gray-300">{agent.name}</span>
            <span className="text-[9px] text-gray-400">·</span>
            <span className="text-[9px] text-gray-400">{agent.role}</span>
          </div>
        ))}
      </div>

      {/* Progress */}
      {task.status === 'in_progress' && (
        <div className="flex items-center gap-2 mb-2.5">
          <div className="flex-1 h-1.5 bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${task.progress}%` }} />
          </div>
          <span className="text-xs text-gray-400 tabular-nums">{task.progress}%</span>
        </div>
      )}

      {/* Footer: stage + reward + deadline */}
      <div className="flex items-center gap-2 flex-wrap">
        {task.stage && (
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
            <Icon name={cfg.icon} size={11} />
            {task.stage}
          </span>
        )}
        {task.reward && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500">
            <Icon name="payments" size={11} />
            {task.reward}
          </span>
        )}
        {task.deadline && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-500">
            <Icon name="schedule" size={11} />
            {task.deadline}
          </span>
        )}
      </div>
    </div>
  );
}
